import { useEffect, useMemo } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { websocketService } from '../../services/websocket';
import { transactionsApi, Transaction } from '../../api';

interface FormattedTransaction extends Transaction {
  formattedDate: string;
  formattedAmount: string;
  statusColor: string;
}

// Utility functions
const formatDate = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    if (!isValid(date)) {
      console.warn('Invalid date:', dateString);
      return 'Invalid date';
    }
    return format(date, 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'confirmed':
    case 'active':
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'canceled':
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'past_due':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function RecentTransactions({ businessId, facilityId }: { businessId: string, facilityId: string }) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data: transactions, isLoading, error } = useQuery<Transaction[]>({
    queryKey: ['transactions', facilityId],
    queryFn: () => transactionsApi.getRecentTransactions(businessId, facilityId),
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Memoize formatted transactions
  const formattedTransactions = useMemo<FormattedTransaction[]>(() => {
    if (!transactions) return [];
    return transactions.map((t: Transaction) => ({
      ...t,
      formattedDate: formatDate(t.createdAt),
      formattedAmount: formatCurrency(t.amount),
      statusColor: getStatusColor(t.status)
    }));
  }, [transactions]);

  useEffect(() => {
    const handleBookingUpdate = (data: any) => {
      queryClient.setQueryData(['transactions', facilityId], (oldData: Transaction[] | undefined) => {
        if (!oldData) return oldData;
        
        const newTransaction: Transaction = {
          id: data.receiptId,
          date: data.date,
          createdAt: data.createdAt,
          amount: data.price / 100,
          type: 'public_use',
          status: data.status,
          customerName: data.guestDetails.name,
          description: 'Guest Court Booking'
        };

        return [newTransaction, ...oldData];
      });
    };

    websocketService.subscribe('booking_update', handleBookingUpdate);
    return () => websocketService.unsubscribe('booking_update', handleBookingUpdate);
  }, [queryClient, facilityId]);

  return (
    <div className="bg-white shadow-lg rounded-2xl border border-gray-100 p-0 mb-8">
      <div className="flex items-center gap-3 px-8 pt-8 pb-2">
        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-indigo-50">
          <BanknotesIcon className="h-6 w-6 text-indigo-500" />
        </span>
        <div>
          <h3 className="text-2xl font-bold text-gray-900 leading-tight">Recent Transactions</h3>
          <p className="text-sm text-gray-500 mt-1">Payments from the last 7 days</p>
        </div>
      </div>
      <div className="border-t border-gray-100 mt-4" />
      <div className="px-8 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[120px]">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error instanceof Error ? error.message : 'Failed to load recent transactions'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formattedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No transactions in the last 7 days
                    </td>
                  </tr>
                ) : (
                  formattedTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.formattedDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.customerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          switch (transaction.type) {
                            case 'subscription':
                              return 'Subscription';
                            case 'public_use':
                              return 'Guest Court Booking';
                            case 'stringing':
                              return 'Stringing Order';
                            case 'event_signup':
                              return 'Event Signup';
                            default:
                              return transaction.type;
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.eventName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.formattedAmount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.statusColor}`}>
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
} 